import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentHistoryItem,
  DocumentTab,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import {
  allDocumentsHaveRevisionCodes,
  getLatestDocumentByRevision,
} from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import { useCallback } from 'react'
import { DocumentViewerAssetType } from 'types'

import { useOcrRefsCount } from './useOcrRefsCount'
import { OcrResultWithPage, useOcrResults } from './useOcrResults'

export type LinkedDocumentsNavigationParams = {
  ocrResult: OcrResultWithPage
  forceNewTab: boolean
}

export const useLinkedDocumentsNavigation = () => {
  const {
    activeTabId,
    setPagination: setGlobalPagination,
    pushToHistory,
    addDocumentTab,
    addTab,
  } = useKnowledgeDocumentUXContext()

  const { systemDocumentEntries } = useKnowledgeDocumentDataContext()

  const { filteredOcrResults } = useOcrResults()
  const { ocrResultRefsMap, getLinkedDocumentsForResult } =
    useOcrRefsCount(filteredOcrResults)

  const navigateOrOpenDocument = useCallback(
    (documentId: string, forceNewTab: boolean) => {
      if (forceNewTab) {
        addDocumentTab({ documentId, pageNumber: 1, selectedBoxIds: [] })
      } else {
        const newDocItem: DocumentHistoryItem = {
          type: 'document',
          documentId,
          pageNumber: 1,
          selectedBoxIds: [],
          query: '',
        }
        pushToHistory(activeTabId, newDocItem)
      }
    },
    [addDocumentTab, pushToHistory, activeTabId]
  )

  const showLinkedDocsTable = useCallback(
    (documentIds: string[], referenceText: string, forceNewTab: boolean) => {
      setGlobalPagination({ pageIndex: 0, pageSize: 200 })
      const linkedDocsItem: SearchHistoryItem = {
        type: 'search',
        searchState: {
          searchMode: 'keyword',
          query: '',
          semanticQuery: '',
          filters: [],
          sorting: [],
          pagination: { pageIndex: 0, pageSize: 200 },
          selectedDocumentId: null,
          isPreviewOpen: false,
        },
        linkedDocuments: {
          referenceText,
          documentIds,
        },
      }

      if (forceNewTab) {
        const newTab: DocumentTab = {
          id: `linked-docs-${Date.now()}`,
          history: [linkedDocsItem],
          currentIndex: 0,
        }
        addTab(newTab)
      } else {
        pushToHistory(activeTabId, linkedDocsItem)
      }
    },
    [setGlobalPagination, pushToHistory, activeTabId, addTab]
  )

  /**
   * Navigates to linked documents for a given OCR result.
   * Returns true if navigation was handled, false otherwise.
   */
  const navigateToLinkedDocuments = useCallback(
    ({ ocrResult, forceNewTab }: LinkedDocumentsNavigationParams): boolean => {
      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        const boxText = (ocrResult.text ?? '').toLowerCase()
        const linkedDocuments = systemDocumentEntries.filter((document) => {
          const title = document.entry.data.title?.toLowerCase() ?? ''
          const drawingNumber =
            document.entry.data.drawing_number?.toLowerCase() ?? ''
          return title.includes(boxText) || drawingNumber.includes(boxText)
        })

        if (linkedDocuments.length === 0) return false

        if (linkedDocuments.length > 1) {
          if (allDocumentsHaveRevisionCodes(linkedDocuments)) {
            const latestDocument = getLatestDocumentByRevision(linkedDocuments)
            if (!latestDocument) return false
            navigateOrOpenDocument(latestDocument.entry.id, forceNewTab)
            return true
          }

          // Multiple documents without revision codes - show in table
          const documentIds = linkedDocuments.map((doc) => doc.entry.id)
          showLinkedDocsTable(documentIds, ocrResult.text ?? '', forceNewTab)
          return true
        }

        // Single linked document - navigate directly
        navigateOrOpenDocument(linkedDocuments[0].entry.id, forceNewTab)
        return true
      }

      // Handle regular linked documents (from refs)
      const linkedDocs = getLinkedDocumentsForResult(ocrResult)
      if (!linkedDocs.length) return false

      const linkedDocumentIds = new Set(
        linkedDocs.map((doc) => doc.entry.tags['system-document'])
      )

      const linkedSystemDocumentIds = systemDocumentEntries
        .filter((entry) => linkedDocumentIds.has(entry.entry.id))
        .map((entry) => entry.entry.id)

      if (linkedSystemDocumentIds.length === 0) return false

      showLinkedDocsTable(
        linkedSystemDocumentIds,
        ocrResult.text ?? '',
        forceNewTab
      )
      return true
    },
    [
      getLinkedDocumentsForResult,
      systemDocumentEntries,
      navigateOrOpenDocument,
      showLinkedDocsTable,
    ]
  )

  const hasLinkedDocuments = useCallback(
    (ocrResult: OcrResultWithPage): boolean => {
      const key = `${ocrResult.id}-${ocrResult.pageNumber}`
      const refsCount = ocrResultRefsMap.get(key) ?? 0

      if (refsCount > 0) return true

      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        const boxText = (ocrResult.text ?? '').toLowerCase()
        const linkedDocuments = systemDocumentEntries.filter((document) => {
          const title = document.entry.data.title?.toLowerCase() ?? ''
          const drawingNumber =
            document.entry.data.drawing_number?.toLowerCase() ?? ''
          return title.includes(boxText) || drawingNumber.includes(boxText)
        })
        return linkedDocuments.length > 0
      }

      return false
    },
    [ocrResultRefsMap, systemDocumentEntries]
  )

  return {
    navigateToLinkedDocuments,
    hasLinkedDocuments,
    ocrResultRefsMap,
    getLinkedDocumentsForResult,
    navigateOrOpenDocument,
    showLinkedDocsTable,
  }
}
